import get from "lodash/get";
import { REFRESH_QUOTE, INITIAL_STATE_LOAD_FINISHED, refreshQuote } from "QuotePage/Actions";
import { getVisiblePlans, getMedicalPlanEnrolledId } from "QuotePage/Selectors";
import { shoppingCartURL, isRenewal, isAQuotePage } from "QuotePage/urls";
import { takeLatest, delay } from "redux-saga";
import { put, take, select, call } from "redux-saga/effects";
import fetchWithCSRFToken from "Utils/fetchWithCSRFToken";
import { removeURLBasename } from "Utils/removeURLBasename";

import { CART_UPDATE_SUCCESS } from "Common/CartService";
import { ALL_CHANGE_ACTIONS as EMPLOYEE_PROFILE_ALL_CHANGE_ACTIONS, validateEmployeeProfile } from "Common/EnterAges/Actions";
import { updateEmployeeProfileApi } from "Common/EnterAges/api";
import { getEmployeeProfile, isEmployeeProfileValidExcludingAge, isEmployeeProfileTouched } from "Common/EnterAges/Selectors";
import { initializePlans } from "Common/PlanLoader/Actions";
import { requestPricingFor, updatePlansPricesLoading } from "Common/PlanLoader/Sagas";
import { appendAuthParams } from "Common/TokenAuth/Utils";

import { history } from "./Store";

export function* watchInitialStateLoadFinished() {
  yield takeLatest(INITIAL_STATE_LOAD_FINISHED, function* () {
    yield put(validateEmployeeProfile());

    const companyProfile = yield select((state) => state.companyProfile);
    if (companyProfile.zip_code_number) {
      yield put(initializePlans(companyProfile.id_token));
    }
  });
}

export function* watchRefreshQuote() {
  yield takeLatest(REFRESH_QUOTE, function* () {
    // Wait to allow repeated requests to settle down a bit.
    yield delay(500);
    const medicalPlanEnrolledId = yield select(getMedicalPlanEnrolledId);
    yield updatePlansPricesLoading({ excludeIds: [medicalPlanEnrolledId] });
    while (true) {
      const visiblePlans = yield select(getVisiblePlans);
      const plansNeedingUpdate = visiblePlans.filter((p) => p.prices_loading);
      if (plansNeedingUpdate.length > 0) {
        const isValid = yield select(isEmployeeProfileValidExcludingAge);
        const isTouched = yield select(isEmployeeProfileTouched);
        if (isValid || !isTouched) {
          const employeeProfile = yield select(getEmployeeProfile);
          const { id_token: companyIDToken } = yield select((state) => state.companyProfile);

          const planIDs = plansNeedingUpdate.map((plan) => plan.id);
          yield requestPricingFor(employeeProfile, planIDs, companyIDToken);
          // Immediately recheck if new plans are visible because updating the
          // price might have caused some plans to jump off the page, e.g. if
          // sorting by monthly cost.
          continue;
        }
      }

      // Wait for the next action to see if the visible plans have changed.
      yield take("*");
    }
  });
}

export function* watchEmployeeProfileChanged() {
  yield takeLatest(EMPLOYEE_PROFILE_ALL_CHANGE_ACTIONS, function* () {
    yield call(delay, 850);
    yield put(refreshQuote());
    const isValid = yield select(isEmployeeProfileValidExcludingAge);

    if (isValid) {
      // Wait a bit to prevent too many price requests when typing, e.g. don't
      // send requests for "4" and then "45" as the user is typing "45".
      const employeeProfile = yield select(getEmployeeProfile);
      const { id_token } = yield select((state) => state.companyProfile);
      yield call(updateEmployeeProfileApi, employeeProfile, id_token);
    }
  });
}

function cartUpdateSideEffects(companyIdToken, analytics) {
  return fetchWithCSRFToken(`/small-group/select-plan/cart-update-side-effects?company_id_token=${companyIdToken}`, {
    method: "POST",
    body: JSON.stringify({
      analytics,
    }),

    processData: false,
  }).then((response) => response.json());
}

export function* watchCartUpdateSuccessInQuotePage() {
  yield takeLatest(CART_UPDATE_SUCCESS, function* (action) {
    if (!isAQuotePage() && !isRenewal()) {
      return;
    }
    const { originalAction } = action.payload;
    // companyIdToken needs to be part of the originalAction
    const companyProfile = yield select((state) => state.companyProfile);
    const companyIdToken = companyProfile.id_token || companyProfile.idToken;
    const { analytics } = originalAction.meta;
    yield call(cartUpdateSideEffects, companyIdToken, analytics);

    if (!originalAction.continueToCart) {
      return;
    }

    let url = appendAuthParams(shoppingCartURL(companyIdToken, isRenewal()));
    if (originalAction.url !== null && originalAction.url !== undefined) {
      url = appendAuthParams(originalAction.url);
    }
    if (isRenewal()) {
      window.location = url;
    } else {
      history.push(removeURLBasename(url));
    }
  });
}
